/**
 * Prefetch de chunks de rutas lazy.
 * Al expandir una sección del sidebar se precargan los imports de sus subpáginas
 * para que el primer clic sea más rápido (chunk ya en caché o descargando).
 *
 * Las rutas estáticas (Dashboard, POS, Menú, Mesas, etc.) ya van en el bundle
 * principal, por eso cargan al instante. Las que usan lazy() requieren descargar
 * el chunk la primera vez; este prefetch reduce la espera al abrir la sección.
 */

type SectionKey =
  | 'orders'
  | 'products'
  | 'reports'
  | 'onlineStore'
  | 'pos'
  | 'loyalty'
  | 'cod'
  | 'crm'
  | 'callCenter'
  | 'operaciones'
  | 'entregas'
  | 'transporte'
  | 'chatbot'
  | 'agents'
  | 'settings';

const prefetchedSections = new Set<SectionKey>();

function prefetch(promise: () => Promise<unknown>) {
  promise().catch(() => {});
}

export function prefetchSection(section: SectionKey): void {
  if (prefetchedSections.has(section)) return;
  prefetchedSections.add(section);

  switch (section) {
    case 'orders':
      prefetch(() => import('@/pages/sales/SalesOrdersPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceOrdersPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceAbandonedCartsPage'));
      prefetch(() => import('@/pages/CustomersPage'));
      break;
    case 'products':
      prefetch(() => import('@/pages/products/CategoryEditPage'));
      prefetch(() => import('@/pages/products/ModifierEditPage'));
      prefetch(() => import('@/pages/products/ProductEditPage'));
      prefetch(() => import('@/pages/products/ProductInventoryDetailPage'));
      break;
    case 'reports':
      prefetch(() => import('@/pages/sales/HeatMapPage'));
      prefetch(() => import('@/pages/sales/RankingPage'));
      prefetch(() => import('@/pages/sales/StatisticsPage'));
      break;
    case 'onlineStore':
      prefetch(() => import('@/pages/ecommerce/EcommerceThemesPage'));
      prefetch(() => import('@/pages/ecommerce/EcommercePagesPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceMenusPage'));
      prefetch(() => import('@/pages/online-store/OnlineStorePreferencesPage'));
      break;
    case 'pos':
      // POS, Mesas, KDS, Menú: la mayoría son estáticos; solo prefetch por si hay rutas lazy anidadas
      break;
    case 'loyalty':
      prefetch(() => import('@/pages/loyalty/LoyaltyDashboardPage'));
      prefetch(() => import('@/pages/loyalty/LoyaltyCustomersPage'));
      prefetch(() => import('@/pages/loyalty/LoyaltySettingsPage'));
      prefetch(() => import('@/pages/loyalty/LoyaltyQRScannerPage'));
      break;
    case 'cod':
      prefetch(() => import('@/pages/cod/CodPage'));
      prefetch(() => import('@/pages/cod/CodDesignPage'));
      prefetch(() => import('@/pages/cod/CodShippingPage'));
      prefetch(() => import('@/pages/cod/CodAnalyticsPage'));
      prefetch(() => import('@/pages/cod/CodSettingsPage'));
      prefetch(() => import('@/pages/cod/CodFraudPage'));
      prefetch(() => import('@/pages/cod/CodDeliverySuccessPage'));
      prefetch(() => import('@/pages/cod/CodSalesBoosterPage'));
      prefetch(() => import('@/pages/cod/CodBillingPage'));
      break;
    case 'crm':
      prefetch(() => import('@/pages/crm/CrmLeadsPage'));
      prefetch(() => import('@/pages/crm/CrmKanbanPage'));
      prefetch(() => import('@/pages/crm/CrmDialerPage'));
      prefetch(() => import('@/pages/crm/CrmLeadDetailPage'));
      prefetch(() => import('@/pages/crm/CrmCallsPage'));
      break;
    case 'callCenter':
      prefetch(() => import('@/pages/crm/CallCenterExtensionsPage'));
      prefetch(() => import('@/pages/crm/CallCenterCampaignsPage'));
      prefetch(() => import('@/pages/crm/CallCenterCampaignDetailPage'));
      prefetch(() => import('@/pages/crm/CallCenterStatsPage'));
      prefetch(() => import('@/pages/settings/SettingsCallCenterPage'));
      break;
    case 'operaciones':
      prefetch(() => import('@/pages/operaciones/PedidosPage'));
      prefetch(() => import('@/pages/operaciones/VentasPage'));
      prefetch(() => import('@/pages/operaciones/OrderDetailPage'));
      prefetch(() => import('@/pages/operaciones/CreateOrderPage'));
      prefetch(() => import('@/pages/operaciones/OpsLogisticaPage'));
      prefetch(() => import('@/pages/operaciones/OpsCobranzasPage'));
      break;
    // ADR 0029: Entregas absorbió Transporte. Las cinco pantallas del módulo del ENVÍO se precargan
    // juntas porque el operador de logística entra y las recorre todas en el mismo turno.
    case 'entregas':
      prefetch(() => import('@/pages/entregas/EntregasPage'));
      prefetch(() => import('@/pages/operaciones/OpsLogisticaPage'));
      prefetch(() => import('@/pages/operaciones/EntregasRecaudoPage'));
      break;
    case 'transporte':
      prefetch(() => import('@/pages/transport/TransportAgenciasPage'));
      prefetch(() => import('@/pages/transport/TransportMotorizadosPage'));
      break;
    case 'chatbot':
      prefetch(() => import('@/pages/chatbot/ChatbotsListPage'));
      prefetch(() => import('@/pages/chatbot/ChatbotEditorPage'));
      break;
    case 'agents':
      prefetch(() => import('@/pages/agents/AgentsStudioIndexPage'));
      prefetch(() => import('@/pages/agents/AgentsCatalogPage'));
      break;
    case 'settings':
      prefetch(() => import('@/pages/SettingsPage'));
      prefetch(() => import('@/pages/UsersPage'));
      prefetch(() => import('@/pages/settings/SettingsUsersRolesPage'));
      prefetch(() => import('@/pages/settings/SettingsUsersSecurityPage'));
      prefetch(() => import('@/pages/ecommerce/EcommercePaymentMethodsPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceCheckoutPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceDeliveryMethodsPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceDeliveryRulesPage'));
      prefetch(() => import('@/pages/ecommerce/LogisticsCouriersCoveragePage'));
      prefetch(() => import('@/pages/ecommerce/LogisticsCourierDetailPage'));
      prefetch(() => import('@/pages/ecommerce/LogisticsCourierRulesPage'));
      prefetch(() => import('@/pages/ecommerce/ShalomAgenciesPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceShippingMethodsPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceDomainsPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceDomainConfigurePage'));
      prefetch(() => import('@/pages/ecommerce/config/EcommerceNotificationsPage'));
      prefetch(() => import('@/pages/ecommerce/EcommerceBrandingPage'));
      prefetch(() => import('@/pages/ModulesPage'));
      prefetch(() => import('@/pages/settings/SettingsPlanPage'));
      break;
    default:
      break;
  }
}

let lowPriorityAdminChunksPrefetched = false;

/**
 * Rutas admin frecuentes que no tienen sección propia en el sidebar expand.
 * Se dispara una sola vez (p. ej. tras login) para acercar el panel al estándar prefetch+SPA.
 */
export function prefetchLowPriorityAdminChunks(): void {
  if (lowPriorityAdminChunksPrefetched) return;
  lowPriorityAdminChunksPrefetched = true;
  prefetch(() => import('@/pages/CustomersPage'));
  prefetch(() => import('@/pages/ContentPage'));
  prefetch(() => import('@/pages/MarketingPage'));
  prefetch(() => import('@/pages/DiscountsPage'));
  prefetch(() => import('@/pages/whatsapp/WhatsAppInboxPage'));
}
